import type { Node, Edge } from '@xyflow/react';
import { INTERNET_NODE_ID } from './constants';
import type { EntityKindAwareNodeData, NodePosition } from './layout-types';

const RADIAL_ROOT_RING_RADIUS = 680;
const RADIAL_RING_GAP = 460;
const RADIAL_MAX_WEDGE = Math.PI * 1.1;
const RADIAL_MIN_SIBLING_ANGLE = Math.PI / 22;
const RADIAL_DENSE_GROUP_RING_GAP = 210;

type RadialNodePlacement = {
  rootId: string;
  depth: number;
  parentId?: string;
};

type RadialDepthInput<TNodeData extends EntityKindAwareNodeData> = {
  node: Node<TNodeData> | undefined;
  rootId: string;
  depth: number;
};

function getRootNodeIds({ nodes, edges }: { nodes: Node[]; edges: Edge[] }) {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const internetRootIds = edges
    .filter(
      (edge) => edge.source === INTERNET_NODE_ID && nodeIds.has(edge.target),
    )
    .map((edge) => edge.target)
    .sort();

  if (internetRootIds.length > 0) {
    return internetRootIds;
  }

  const targetIds = new Set(
    edges
      .filter((edge) => nodeIds.has(edge.source) && nodeIds.has(edge.target))
      .map((edge) => edge.target),
  );
  const entryPointIds = nodes
    .filter((node) => node.id !== INTERNET_NODE_ID && !targetIds.has(node.id))
    .map((node) => node.id)
    .sort();

  return entryPointIds.length > 0
    ? entryPointIds
    : nodes
        .filter((node) => node.id !== INTERNET_NODE_ID)
        .map((node) => node.id)
        .sort();
}

function getRadialNodeDepth<TNodeData extends EntityKindAwareNodeData>({
  node,
  rootId,
  depth,
}: RadialDepthInput<TNodeData>) {
  if (!node || node.id === rootId) return depth;
  const { entityKind } = node.data;

  if (entityKind === 'HOST') {
    return 2;
  }

  if (entityKind === 'NETWORK_SERVICE') {
    return Math.max(depth, 3);
  }

  if (entityKind === 'SOFTWARE') {
    return Math.max(depth, 4);
  }

  if (entityKind === 'DOMAIN') {
    return Math.max(depth, 5);
  }

  return depth;
}

function getRadialPlacements<TNodeData extends EntityKindAwareNodeData>({
  nodes,
  edges,
  rootNodeIds,
}: {
  nodes: Node<TNodeData>[];
  edges: Edge[];
  rootNodeIds: string[];
}) {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const adjacency = new Map<string, string[]>();
  for (const edge of edges) {
    if (
      edge.source === INTERNET_NODE_ID ||
      edge.target === INTERNET_NODE_ID ||
      !nodeIds.has(edge.source) ||
      !nodeIds.has(edge.target)
    ) {
      continue;
    }

    const sourceNeighbors = adjacency.get(edge.source) ?? [];
    sourceNeighbors.push(edge.target);
    adjacency.set(edge.source, sourceNeighbors);

    const targetNeighbors = adjacency.get(edge.target) ?? [];
    targetNeighbors.push(edge.source);
    adjacency.set(edge.target, targetNeighbors);
  }

  const placements = new Map<string, RadialNodePlacement>();
  for (const rootId of rootNodeIds) {
    const queue: Array<{ nodeId: string; depth: number; parentId?: string }> = [
      { nodeId: rootId, depth: 1 },
    ];

    while (queue.length > 0) {
      const next = queue.shift();
      if (!next || placements.has(next.nodeId)) continue;
      const depth = getRadialNodeDepth({
        node: nodesById.get(next.nodeId),
        rootId,
        depth: next.depth,
      });
      placements.set(next.nodeId, {
        rootId,
        depth,
        parentId: next.parentId,
      });

      for (const neighborId of adjacency.get(next.nodeId) ?? []) {
        if (!placements.has(neighborId)) {
          queue.push({
            nodeId: neighborId,
            depth: next.depth + 1,
            parentId: next.nodeId,
          });
        }
      }
    }
  }

  for (const node of nodes) {
    if (node.id === INTERNET_NODE_ID || placements.has(node.id)) continue;
    placements.set(node.id, {
      rootId: node.id,
      depth: getRadialNodeDepth({ node, rootId: node.id, depth: 1 }),
    });
  }

  return placements;
}

function getConnectedIpByNodeId<TNodeData extends EntityKindAwareNodeData>({
  nodes,
  edges,
}: {
  nodes: Node<TNodeData>[];
  edges: Edge[];
}) {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const connectedIpByNodeId = new Map<string, string>();

  for (const edge of edges) {
    const sourceNode = nodesById.get(edge.source);
    const targetNode = nodesById.get(edge.target);
    if (!sourceNode || !targetNode) continue;

    const { entityKind: sourceEntityKind } = sourceNode.data;
    const { entityKind: targetEntityKind } = targetNode.data;

    if (sourceEntityKind === 'HOST' && targetEntityKind !== 'HOST') {
      connectedIpByNodeId.set(targetNode.id, sourceNode.id);
    }

    if (targetEntityKind === 'HOST' && sourceEntityKind !== 'HOST') {
      connectedIpByNodeId.set(sourceNode.id, targetNode.id);
    }
  }

  return connectedIpByNodeId;
}

export function getRadialLayout<TNodeData extends EntityKindAwareNodeData>({
  nodes,
  edges,
}: {
  nodes: Node<TNodeData>[];
  edges: Edge[];
}) {
  const computedLayout = new Map<string, NodePosition>();
  const rootNodeIds = getRootNodeIds({ nodes, edges });
  const placements = getRadialPlacements({ nodes, edges, rootNodeIds });
  const connectedIpByNodeId = getConnectedIpByNodeId({ nodes, edges });
  const nodesByRootAndDepth = new Map<string, Map<number, string[]>>();

  for (const [nodeId, placement] of placements.entries()) {
    const nodesByDepth =
      nodesByRootAndDepth.get(placement.rootId) ?? new Map<number, string[]>();
    const depthNodes = nodesByDepth.get(placement.depth) ?? [];
    depthNodes.push(nodeId);
    nodesByDepth.set(placement.depth, depthNodes);
    nodesByRootAndDepth.set(placement.rootId, nodesByDepth);
  }

  const layoutRootIds = Array.from(nodesByRootAndDepth.keys()).sort();
  if (nodes.some((node) => node.id === INTERNET_NODE_ID)) {
    computedLayout.set(INTERNET_NODE_ID, { x: 0, y: 0 });
  }

  layoutRootIds.forEach((rootId, rootIndex) => {
    const rootAngle =
      -Math.PI / 2 + (rootIndex / layoutRootIds.length) * Math.PI * 2;
    const rootSector = (Math.PI * 2) / layoutRootIds.length;
    const maxWedge = Math.min(RADIAL_MAX_WEDGE, rootSector * 0.85);
    const nodesByDepth = nodesByRootAndDepth.get(rootId);
    if (!nodesByDepth) return;
    const angleByNodeId = new Map<string, number>([[rootId, rootAngle]]);
    const sortedDepths = Array.from(nodesByDepth.keys()).sort(
      (left, right) => left - right,
    );

    for (const depth of sortedDepths) {
      const sortedNodeIds = [...(nodesByDepth.get(depth) ?? [])].sort();
      const radius = RADIAL_ROOT_RING_RADIUS + (depth - 1) * RADIAL_RING_GAP;

      const nodeIdsByAnchor = new Map<string, string[]>();
      for (const nodeId of sortedNodeIds) {
        const placement = placements.get(nodeId);
        const connectedIpId = connectedIpByNodeId.get(nodeId);
        const connectedIpPlacement = connectedIpId
          ? placements.get(connectedIpId)
          : undefined;
        const anchorId =
          connectedIpId &&
          connectedIpPlacement?.rootId === rootId &&
          angleByNodeId.has(connectedIpId)
            ? connectedIpId
            : placement?.parentId && angleByNodeId.has(placement.parentId)
              ? placement.parentId
              : rootId;
        const anchorNodeIds = nodeIdsByAnchor.get(anchorId) ?? [];
        anchorNodeIds.push(nodeId);
        nodeIdsByAnchor.set(anchorId, anchorNodeIds);
      }

      for (const [anchorId, anchorNodeIds] of nodeIdsByAnchor.entries()) {
        const anchorAngle = angleByNodeId.get(anchorId) ?? rootAngle;
        const anchorWedge =
          anchorId === rootId
            ? maxWedge
            : Math.min(
                maxWedge * 0.8,
                RADIAL_MIN_SIBLING_ANGLE *
                  Math.max(anchorNodeIds.length - 1, 1),
              );
        const nodesPerRing = Math.max(
          1,
          Math.floor(anchorWedge / RADIAL_MIN_SIBLING_ANGLE) + 1,
        );
        anchorNodeIds.forEach((nodeId, nodeIndex) => {
          const ringIndex = Math.floor(nodeIndex / nodesPerRing);
          const indexInRing = nodeIndex % nodesPerRing;
          const nodesInRing = Math.min(
            nodesPerRing,
            anchorNodeIds.length - ringIndex * nodesPerRing,
          );
          const angleStep =
            nodesInRing > 1 ? anchorWedge / (nodesInRing - 1) : 0;
          const firstAngle = anchorAngle - anchorWedge / 2;
          const angle =
            nodesInRing > 1
              ? firstAngle + indexInRing * angleStep
              : anchorAngle;
          const nodeRadius = radius + ringIndex * RADIAL_DENSE_GROUP_RING_GAP;

          angleByNodeId.set(nodeId, angle);
          computedLayout.set(nodeId, {
            x: Math.cos(angle) * nodeRadius,
            y: Math.sin(angle) * nodeRadius,
          });
        });
      }
    }
  });

  return computedLayout;
}
